/**
 * Reading Progress Service
 * Handles reading progress tracking state from backend WebSocket messages
 */

export interface ReadingProgressData {
  current_word_index: number;
  total_words: number;
  completion_percentage: number;
  words_read: number;
  story_session_id?: string; // Added for summary generation
}

export interface NextWordData {
  next_word: string;
  syllables: string;
  word_index: number;
  completion_percentage: number;
  context_before?: string;
  context_after?: string;
}

export interface ReadingProgressUpdate {
  type: 'reading_progress';
  conversation_id: string;
  progress: ReadingProgressData;
  next_word: NextWordData | null;
  context: {
    previous: string;
    upcoming: string;
  };
}

class ReadingProgressService {
  private listeners: Set<(update: ReadingProgressUpdate) => void> = new Set();
  private currentProgress: ReadingProgressUpdate | null = null;

  /**
   * Subscribe to reading progress updates
   */
  subscribe(callback: (update: ReadingProgressUpdate) => void): () => void {
    this.listeners.add(callback);
    
    // Send current state immediately if available
    if (this.currentProgress) {
      callback(this.currentProgress);
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Handle reading progress update from WebSocket
   */
  handleProgressUpdate(update: ReadingProgressUpdate): void {
    this.currentProgress = update;
    
    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(update);
      } catch (error) {
        console.error('Error in reading progress listener:', error);
      }
    });
  }

  /**
   * Get current progress state
   */
  getCurrentProgress(): ReadingProgressUpdate | null {
    return this.currentProgress;
  }

  /**
   * Clear progress (e.g., when story changes)
   */
  clearProgress(): void {
    this.currentProgress = null;
    this.listeners.forEach(listener => {
      try {
        listener(null as any);
      } catch (error) {
        console.error('Error clearing reading progress listener:', error);
      }
    });
  }

  /**
   * Fetch current progress from REST API (fallback method)
   */
  async fetchProgress(conversationId: string): Promise<ReadingProgressUpdate | null> {
    try {
      const response = await fetch(`/voicelive/reading/progress/${conversationId}`);
      const data = await response.json();
      
      if (data.success && data.current_word_index !== undefined) {
        // Transform REST API response to match WebSocket format
        const update: ReadingProgressUpdate = {
          type: 'reading_progress',
          conversation_id: conversationId,
          progress: {
            current_word_index: data.current_word_index,
            total_words: data.total_words,
            completion_percentage: data.completion_percentage,
            words_read: data.words_read
          },
          next_word: null,
          context: {
            previous: '',
            upcoming: ''
          }
        };
        
        this.currentProgress = update;
        return update;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to fetch reading progress:', error);
      return null;
    }
  }

  /**
   * Fetch next word with pronunciation help from REST API
   */
  async fetchNextWord(conversationId: string): Promise<NextWordData | null> {
    try {
      const response = await fetch(`/voicelive/reading/next-word/${conversationId}`);
      const data = await response.json();
      
      if (data.success && data.next_word) {
        return {
          next_word: data.next_word,
          syllables: data.syllables,
          word_index: data.word_index,
          completion_percentage: data.completion_percentage,
          context_before: data.context_before,
          context_after: data.context_after
        };
      }
      
      return null;
    } catch (error) {
      console.error('Failed to fetch next word:', error);
      return null;
    }
  }
}

// Export singleton instance
export const readingProgressService = new ReadingProgressService();
